import type { AgentTokenExpiryDays, AgentTokenScope } from 'definitions/agent-tokens';
import type { AuthenticationResponseJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import type { AgentTokenOptions } from '@/state/useAgentTokens';
import type { WebsiteMode } from '../Dashboard/Settings/AgentAccess/use-agent-access';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WebAuthnError, startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import {
  DEFAULT_AGENT_TOKEN_EXPIRY_DAYS,
  MAX_AGENT_TOKEN_LIMIT_IDS,
  STAFF_TOKEN_REFUSAL_MESSAGES,
  isStaffWriteToken,
  staffTokenRefusal,
} from 'definitions/agent-tokens';
import { apiErrorMessage } from '@/state/api-error';
import { AGENT_ACCESS_COPY } from '../Dashboard/Settings/AgentAccess/agent-access-copy';
import { CONNECT_COPY } from './connect-copy';

/** What the server says about the app asking (src/servers/oauth/client-policy.ts, clientDisplay). */
export type ConsentClient = {
  clientId: string;
  host: string;
  name: string | null;
  logoUri: string | null;
  redirectHost: string;
  loopback: boolean;
  /** On the server's list of checked apps (VERIFIED_CLIENTS); the rest are shown as unverified. */
  verified: boolean;
};

export type ConsentDetails = {
  client: ConsentClient;
  requestedScopes: AgentTokenScope[];
  allowedScopes: AgentTokenScope[];
  expiresAt: number;
  /** Why this person cannot connect this app at all (staff may connect only verified apps), or null. */
  refusal: string | null;
};

type Decision = { redirectTo: string };

/**
 * Only ever follows the server's answer back into the authorization server on this origin, so a
 * bad answer cannot send the browser anywhere else.
 */
export const isResumeUrl = (value: string, origin: string): boolean => {
  try {
    const url = new URL(value);
    return url.origin === origin && url.pathname.startsWith('/oauth/authorize/');
  } catch {
    return false;
  }
};

/**
 * Where the account's passkeys are, as a person would name them: the host for a web origin, the
 * Chrome extension for an extension origin. The token form has the same helper (use-agent-access.ts);
 * it is not imported from there because that module pulls in the whole token form's state, which
 * this page does not otherwise load.
 */
export const passkeyPlaces = (origins: readonly string[]): string[] => [
  ...new Set(
    origins.map((origin) => {
      if (origin.startsWith('chrome-extension://')) return AGENT_ACCESS_COPY.extensionPlace;
      return URL.parse(origin)?.host || origin;
    }),
  ),
];

/**
 * What to say when Allow fails, by the rule of the token form's createFailureMessage (not imported,
 * for the reason above): @simplewebauthn/browser keeps the browser's error name, and neither its text
 * nor the DOMException's is written for a person, so a passkey failure gets our own line. The API's
 * refusals are shown as sent.
 */
export const consentFailureMessage = (error: unknown): string => {
  if (error instanceof WebAuthnError || error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'AbortError'
      ? AGENT_ACCESS_COPY.passkeyCancelled
      : AGENT_ACCESS_COPY.passkeyFailed;
  }
  return apiErrorMessage(error, CONNECT_COPY.connectFailed);
};

type BlockingParams = {
  scopes: readonly AgentTokenScope[];
  websiteMode: WebsiteMode;
  websiteIds: readonly number[];
  expiresInDays: number;
  password: string;
  user: { role?: string | null; companyId?: number | null } | null | undefined;
  /** The account's passkey cannot answer on this site (passkeys are bound to their host). */
  passkeyElsewhere?: boolean;
};

/** Why Allow is dead, in one sentence, or undefined. The server applies the same rules. */
export const consentBlockingReason = ({
  scopes,
  websiteMode,
  websiteIds,
  expiresInDays,
  password,
  user,
  passkeyElsewhere = false,
}: BlockingParams): string | undefined => {
  if (passkeyElsewhere) return CONNECT_COPY.passkeyElsewhereShort;
  if (scopes.length === 0) return AGENT_ACCESS_COPY.needScope;
  if (websiteMode === 'some' && websiteIds.length === 0) return AGENT_ACCESS_COPY.noWebsitesYet;
  if (websiteMode === 'some' && websiteIds.length > MAX_AGENT_TOKEN_LIMIT_IDS) {
    return AGENT_ACCESS_COPY.tooManyWebsites(MAX_AGENT_TOKEN_LIMIT_IDS);
  }
  const refusal = staffTokenRefusal({
    user,
    scopes,
    websiteIds: websiteMode === 'some' ? websiteIds : null,
    companyIds: null,
    expiresInDays,
  });
  if (refusal) return STAFF_TOKEN_REFUSAL_MESSAGES[refusal];
  if (!password) return AGENT_ACCESS_COPY.needPassword;
  return undefined;
};

/**
 * The consent screen's state: what the app asked for, what the person allows, and the decision.
 * The OAuth routes live on this origin under /oauth (not the API base URL), because the
 * authorization server's interaction cookie is only sent there.
 */
export const useConnect = (uid: string) => {
  const { user } = useAuth();
  const oauthClient = useClient('');
  const apiClient = useClient();

  const details = useQuery<ConsentDetails>({
    queryKey: ['OAUTH_CONSENT', uid],
    queryFn: () => oauthClient.get<ConsentDetails>(`/oauth/interaction/${encodeURIComponent(uid)}/details`),
    retry: false,
  });
  const options = useQuery<AgentTokenOptions>({
    queryKey: ['AGENT_TOKENS', 'options'],
    queryFn: () => apiClient.get<AgentTokenOptions>('/agent-tokens/options'),
  });

  // Null until the person touches a box: then everything the app asked for and the role allows.
  const [chosenScopes, setChosenScopes] = useState<AgentTokenScope[] | null>(null);
  const [websiteMode, setWebsiteMode] = useState<WebsiteMode>('some');
  const [websiteIds, setWebsiteIds] = useState<number[]>([]);
  const [chosenExpiry, setChosenExpiry] = useState<AgentTokenExpiryDays | null>(null);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<'allow' | 'deny' | 'leaving' | null>(null);
  // The last answer's failure, shown in the page: a passkey or password refusal must be read there.
  const [actionError, setActionError] = useState<string | null>(null);

  const allowedScopes = details.data?.allowedScopes ?? [];
  const scopes = chosenScopes ?? allowedScopes;
  // As on the token form: a staff connection that can make changes must name its sites (no "every
  // website"), and its expiry is anyone's.
  const staffWrite = isStaffWriteToken({ user, scopes });
  const expiresInDays = chosenExpiry ?? DEFAULT_AGENT_TOKEN_EXPIRY_DAYS;
  // Passkeys are bound to their host, so one registered elsewhere cannot confirm a consent here.
  const passkeyElsewhere = !!options.data?.passkeyStepUp && !options.data.passkeyUsableHere;
  const passkeyNotice = passkeyElsewhere
    ? CONNECT_COPY.passkeyElsewhere(passkeyPlaces(options.data?.passkeyOrigins ?? []))
    : null;
  const blockingReason = consentBlockingReason({
    scopes,
    websiteMode: staffWrite ? 'some' : websiteMode,
    websiteIds,
    expiresInDays,
    password,
    user,
    passkeyElsewhere,
  });

  /** Ticking a write scope on a staff account moves the websites choice to the ones they pick. */
  const toggleScope = (scope: AgentTokenScope) => {
    const next = scopes.includes(scope) ? scopes.filter((item) => item !== scope) : [...scopes, scope];
    setChosenScopes(next);
    if (isStaffWriteToken({ user, scopes: next })) setWebsiteMode('some');
  };

  const toggleWebsite = (websiteId: number) =>
    setWebsiteIds((current) =>
      current.includes(websiteId) ? current.filter((item) => item !== websiteId) : [...current, websiteId],
    );

  /** Every website in the list, as explicit ids; with all of them ticked already, none. */
  const toggleAllWebsites = () => {
    const all = options.data?.websites.map((website) => website.id) ?? [];
    setWebsiteIds((current) => (current.length >= all.length ? [] : all));
  };

  const leave = ({ redirectTo }: Decision) => {
    if (!isResumeUrl(redirectTo, window.location.origin)) throw new Error('Unexpected answer from the server');
    setPending('leaving');
    window.location.assign(redirectTo);
  };

  const confirmWithPasskey = async (): Promise<AuthenticationResponseJSON> => {
    const optionsJSON = await apiClient.post<PublicKeyCredentialRequestOptionsJSON>('/agent-tokens/passkey-challenge');
    return startAuthentication({ optionsJSON });
  };

  const allow = async () => {
    // Without the options, whether a passkey is needed is unknown, and the server would refuse.
    if (blockingReason || pending || !options.data || details.data?.refusal) return;
    setPending('allow');
    setActionError(null);
    try {
      const passkey = options.data?.passkeyStepUp ? await confirmWithPasskey() : undefined;
      const decision = await oauthClient.post<Decision>(`/oauth/interaction/${encodeURIComponent(uid)}/confirm`, {
        scopes,
        websiteIds: staffWrite || websiteMode === 'some' ? websiteIds : null,
        expiresInDays,
        password,
        ...(passkey ? { passkey } : {}),
      });
      leave(decision);
    } catch (error) {
      // In the page, next to Allow: a 403 from the passkey step-up or a wrong password is the answer.
      setActionError(consentFailureMessage(error));
      setPending(null);
    }
  };

  const deny = async () => {
    if (pending) return;
    setPending('deny');
    setActionError(null);
    try {
      leave(await oauthClient.post<Decision>(`/oauth/interaction/${encodeURIComponent(uid)}/deny`, {}));
    } catch (error) {
      setActionError(apiErrorMessage(error, CONNECT_COPY.denyFailed));
      setPending(null);
    }
  };

  return {
    details: details.data,
    isLoading: details.isLoading || options.isLoading,
    loadError: details.isError ? apiErrorMessage(details.error, CONNECT_COPY.loadFailed) : null,
    options: options.data,
    optionsError: options.isError ? CONNECT_COPY.optionsFailed : null,
    retryOptions: () => void options.refetch(),
    allowedScopes,
    scopes,
    toggleScope,
    websiteMode: staffWrite ? ('some' as const) : websiteMode,
    setWebsiteMode,
    staffWrite,
    passkeyNotice,
    actionError,
    websiteIds,
    toggleWebsite,
    toggleAllWebsites,
    expiresInDays,
    setExpiresInDays: setChosenExpiry,
    password,
    setPassword,
    blockingReason,
    pending,
    allow,
    deny,
  };
};

export type ConnectManager = ReturnType<typeof useConnect>;
